import {
  QueryCache,
  MutationCache,
  QueryClient,
  defaultShouldDehydrateQuery,
  isServer,
} from "@tanstack/react-query";
import { showError } from "@/lib/toast";
import { handleSessionExpired } from "@/lib/auth/handle-session-expired";
import { handle403Pivot } from "@/lib/auth/handle-403-pivot";
import {
  isBackendUnavailableError,
  useBackendHealthStore,
} from "@/stores/useBackendHealthStore";

interface ApiError {
  message?: string;
  statusCode?: number;
  /** Stable slug emitted by BE so callers can pivot on the error kind. */
  id?: string;
}

// True when this error came from a 401 the worker proxy could not
// recover via refresh — handle-session-expired owns the user-facing
// reaction (toast + redirect). Other handlers should swallow it so we
// don't double-toast.
function isSessionExpired(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  return (error as ApiError).statusCode === 401;
}

// Cooperative disabled-state errors that the BE emits with a stable
// `error.id` slug so consumers can render an inline CTA ("Enable in
// Settings") instead of a generic toast. Toasting these is hostile UX:
// a disabled org feature is an expected product state, and stale/in-flight
// queries from the previously selected org may resolve after navigation.
// Route guards, FeatureGate, and module-disabled cards own the visible UX.
const COOPERATIVE_DISABLED_SLUGS: ReadonlySet<string> = new Set([
  "feature_not_enabled",
  "media_disabled",
]);

function isCooperativeDisabled(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const id = (error as ApiError).id;
  return typeof id === "string" && COOPERATIVE_DISABLED_SLUGS.has(id);
}

function getErrorMessage(error: unknown): string {
  if (error && typeof error === "object") {
    const apiError = error as ApiError;
    if (apiError.message) {
      return apiError.message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "An error occurred. Please try again.";
}

function makeQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (isBackendUnavailableError(error)) {
          useBackendHealthStore.getState().reportBackendFailure(getErrorMessage(error));
          return;
        }
        if (isSessionExpired(error)) {
          handleSessionExpired();
          return;
        }
        if (isCooperativeDisabled(error)) return;
        // 403 system-tier error.id pivot — redirect or toast for known slugs.
        // Returns true when handled; skip generic error toast to avoid double-notify.
        if ((error as ApiError).statusCode === 403) {
          const handled = handle403Pivot(error as ApiError & { detail?: { feature?: string } });
          if (handled) return;
        }
        if (query.meta?.skipErrorToast) return;
        if (!isServer) {
          showError(getErrorMessage(error));
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (isBackendUnavailableError(error)) {
          useBackendHealthStore.getState().reportBackendFailure(getErrorMessage(error));
          return;
        }
        if (isSessionExpired(error)) {
          handleSessionExpired();
          return;
        }
        if (isCooperativeDisabled(error)) return;
        // 403 system-tier error.id pivot — redirect or toast for known slugs.
        if ((error as ApiError).statusCode === 403) {
          const handled = handle403Pivot(error as ApiError & { detail?: { feature?: string } });
          if (handled) return;
        }
        if (mutation.meta?.skipErrorToast) return;
        if (!isServer) {
          showError(getErrorMessage(error));
        }
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        // Cooperative disabled-state errors are deterministic — the org
        // has a module/feature off and no amount of retry will flip that.
        // Skipping retry avoids extra BE round-trips on a fresh org boot.
        retry: (failureCount, error) => {
          if (isBackendUnavailableError(error)) return false;
          if (isCooperativeDisabled(error)) return false;
          return failureCount < 1;
        },
      },
      dehydrate: {
        shouldDehydrateQuery: (query) =>
          defaultShouldDehydrateQuery(query) ||
          query.state.status === "pending",
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

export function getQueryClient() {
  if (isServer) {
    return makeQueryClient();
  }

  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient();
  }

  return browserQueryClient;
}
